import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ArmedPaxStore } from '../armed-pax.store';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { Observable, Subject, merge, of, switchMap } from 'rxjs';
import { SequenceStore } from '@sequence/sequence.store';
import { ArmedPaxView } from '../models/armed-pax.view';
import { MatDialog } from '@angular/material/dialog';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { SabreText } from '@shared/components/sabre-text-dialog/models/sabre-text';
import { DateTime } from 'luxon';
import { fadeIn } from '@animations/fade-in';

@Component({
  selector: 'app-armed-pax-card',
  templateUrl: './armed-pax-card.component.html',
  styleUrls: ['./armed-pax-card.component.scss'],
  standalone: false,
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArmedPaxCardComponent implements OnInit {
  private _flight$ = this.sequenceStore.flight$;
  private _expanded$ = new Subject<void>(); // Triggered when expansion panel expanded.
  private _refresh$ = new Subject<void>(); // Triggered when refresh button clicked.

  private _getArmedPax$?: Observable<RequestState<ArmedPaxView>>;
  private _refreshArmedPax$?: Observable<RequestState<ArmedPaxView>>;

  armedPax$?: Observable<RequestState<ArmedPaxView>>;

  constructor(
    private armedPaxStore: ArmedPaxStore,
    private sequenceStore: SequenceStore,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this._getArmedPax$ = merge(this._expanded$, this._flight$).pipe(
      switchMap(() => this._flight$),
      switchMap((flight) => {
        if (flight.response) {
          return this.armedPaxStore.getArmedPax(flight.response);
        } else {
          return of(flight.into<ArmedPaxView>());
        }
      }),
    );

    this._refreshArmedPax$ = this._refresh$.pipe(
      switchMap(() => this._flight$),
      switchMap((flight) => {
        if (flight.response) {
          return this.armedPaxStore.getArmedPax(flight.response, true);
        } else {
          return of(flight.into<ArmedPaxView>());
        }
      }),
    );

    this.armedPax$ = merge(this._getArmedPax$, this._refreshArmedPax$);

    // Trigger data load by simulating expansion panel expanded.
    this._expanded$.next();
  }

  onExpanded(): void {
    this._expanded$.next();
  }

  onSabreTextClick(text: string, timestamp: DateTime = DateTime.now()): void {
    const sabreText = new SabreText(text, timestamp.toISO());

    this.dialog.open(SabreTextDialogComponent, {
      data: {
        sabreText$: of(
          new RequestState<SabreText>(
            RequestStatus.IsCompleteWithResponse,
            sabreText,
          ),
        ),
      },
      maxWidth: '100vw',
      maxHeight: '95vh',
    });
  }

  refresh(): void {
    this._refresh$.next();
  }
}
